import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import moment from 'moment';
import FileUpload from './FileUpload';
import '../../styles/documentUpload.css';
import { stateOptions } from '../../config/stateOptions';
import validation from '../../utils/validation';
import { getFileNameFromSignedUrl, convertHeicIfNeeded } from '../../utils/file';
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';

// Configure axios retry
axiosRetry(axios, {
  retries: 2,
  retryDelay: (retryCount) => {
    return retryCount * 1000; // time interval between retries
  },
  retryCondition: (error) => {
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status >= 500; // retry on server errors
  }
});

const DocumentUpload = ({
  documentType,
  documentTypes: propDocumentTypes,
  onFileSubmit,
  uploadFor = '',
  isS3UploadRequired = false, // when true and uploadFor present, upload via backend API
  nurseId = '', // required when isS3UploadRequired is true; used for S3 key prefix
  platform = ''
}) => {
  const REQUEST_ENDPOINT =
    process.env.REACT_APP_COREVERIFY_API ?? 'https://coreverify-api.cloud.nurseio.com';
  const defaultValues = {
    title: '',
    specialties: [],
    jurisdiction: '',
    licenseNumber: '',
    cardNumber: '',
    effectiveDate: '',
    expiryDate: '',
    shareable: 'false',
    fileUploadKeys: [],
    multiState: 'no',
    exemption: false,
    stateIssued: []
  };

  // Upload one or more files via backend API in a single call
  const uploadFilesViaApi = async (filesWithSide = []) => {
    if (!uploadFor || !isS3UploadRequired || !filesWithSide.length) return {};

    try {
      const convertedFiles = [];
      for (const { side, fileData } of filesWithSide) {
        const convertedFileData = await convertHeicIfNeeded(fileData);
        convertedFiles.push({ side, fileData: convertedFileData });
      }

      // Validate file sizes (max 10MB)
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      for (const { side, fileData } of convertedFiles) {
        const fileSize = fileData.binaryData?.byteLength || 0;
        if (fileSize > MAX_FILE_SIZE) {
          throw new Error(
            `File size exceeds 10MB limit. Please choose a smaller file for ${side} attachment.`
          );
        }
      }

      const formData = new FormData();
      formData.append('uploadFor', uploadFor);
      // Send type and nurseId as metadata; backend will generate keys
      if (documentType) formData.append('documentType', documentType);
      if (nurseId) formData.append('nurseId', nurseId);
      if (platform) formData.append('platform', platform);

      convertedFiles.forEach(({ side, fileData }) => {
        // Determine MIME type from file extension
        const ext = fileData.name.split('.').pop().toLowerCase();
        const mimeTypes = {
          jpg: 'image/jpeg',
          jpeg: 'image/jpeg',
          png: 'image/png',
          pdf: 'application/pdf',
          gif: 'image/gif',
          webp: 'image/webp'
        };
        const mimeType = mimeTypes[ext] || 'application/octet-stream';

        const blob = new Blob([fileData.binaryData], { type: mimeType });
        formData.append('files', blob, fileData.name);
        formData.append('sides', side); // 'front' or 'back'
      });

      const res = await fetch(`${REQUEST_ENDPOINT}/api/upload-file`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Upload failed with status ${res.status}`);
      }

      const data = await res.json();
      return { keys: data.keys || [] };
    } catch (err) {
      console.error('Error uploading files:', err);
      setError({
        ...error,
        upload: err.message || 'Failed to upload files to S3. Please try again.'
      });
      throw err;
    }
  };

  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedDocTypeObj, setSelectedDocTypeObj] = useState(null);
  const [docConfig, setDocConfig] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(defaultValues);
  const [showForm, setShowForm] = useState(false);
  const [frontUploaded, setFrontUploaded] = useState(false);
  const [backUploaded, setBackUploaded] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [uploadedFileFront, setUploadedFileFront] = useState(null);
  const [uploadedFileBack, setUploadedFileBack] = useState(null);
  const [showShareable, setShowShareable] = useState(true);
  const [showMultiState, setShowMultiState] = useState(false);
  const [showExemption, setShowExemption] = useState(false);
  const [error, setError] = useState({});
  const [frontUploading, setFrontUploading] = useState(false);
  const [backUploading, setBackUploading] = useState(false);
  const [isPreSelected, setIsPreSelected] = useState(false);
  const [hideTitle, setHideTitle] = useState(false);
  const [hideFileView, setHideFileView] = useState(false);
  // const [isUploading, setIsUploading] = useState(false);

  const fetchDocConfig = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${REQUEST_ENDPOINT}/api/document-config/${id}`);
        const docData = response.data;

        let front_attachment = { required: true, mandatory: false };
        let back_attachment = { required: true, mandatory: false };
        try {
          front_attachment = docData?.front_attachment
            ? JSON.parse(docData.front_attachment)
            : {
                required: true,
                mandatory: false
              };
        } catch (e) {}
        try {
          back_attachment = docData?.back_attachment
            ? JSON.parse(docData.back_attachment)
            : {
                required: true,
                mandatory: false
              };
        } catch (e) {}
        docData.front_attachment = front_attachment;
        docData.back_attachment = back_attachment;
        setDocConfig(docData);
        setSelectedDocTypeObj(docData);

        if (docData.frontFile) {
          setFrontUploaded(true);
        }
        if (docData.backFile) {
          setBackUploaded(true);
        }
      } catch (error) {
        console.error('Error fetching document details:', error);
        setFetchError('Unable to fetch document details. Please try again.');
        setDocConfig(null);
      }
    },
    [REQUEST_ENDPOINT]
  );

  const handleUpdateMetaData = useCallback(
    (entity) => {
      if (!entity.coreVerifyId && entity.id) {
        const defaultDocConfig = {
          title: entity.title || 'Document',
          front_attachment: { required: true, mandatory: true },
          back_attachment: { required: false, mandatory: false },
          require_valid_date: true,
          require_expire_date: true,
          require_valid_date_mandatory: false,
          require_expire_date_mandatory: false,
          require_number: false,
          require_doc_data: false,
          specialities_enabled: false,
          shareable: true,
          verification_config: null
        };
        setDocConfig(defaultDocConfig);
        setSelectedDocTypeObj(entity);
        setHideTitle(true);
        setHideFileView(entity.hideFileView === true);
      } else {
        setSelectedDocType(entity.coreVerifyId);
        setHideFileView(entity.hideFileView === true);
      }

      if (entity.metadata) {
        const selectedOptions = [];
        propDocumentTypes?.[0]?.specialities?.forEach((item) => {
          if (item.isSelected) selectedOptions.push(item.value);
        });
        const formPayload = {
          title: entity?.metadata?.title,
          licenseNumber: entity?.metadata?.licenseNumber || '',
          cardNumber: entity?.metadata?.cardNumber || '',
          effectiveDate: entity?.metadata?.effectiveDate || entity?.metadata?.issueDate || '',
          expiryDate: entity?.metadata?.expiryDate || entity?.metadata?.expirationDate || '',
          shareable: entity?.metadata?.shareable?.toString() || 'false',
          jurisdiction: entity?.metadata?.jurisdiction || '',
          fileUploadKeys: entity?.metadata?.fileUploadKeys || [],
          signedUrls: entity?.metadata?.signedUrls || [],
          verificationSignedUrls: entity?.metadata?.verificationSignedUrls || [],
          showDelete:
            entity?.metadata?.showDelete !== undefined ? entity?.metadata?.showDelete : true,
          specialties: selectedOptions || [],
          multiState: entity?.metadata?.multiState || 'no',
          exemption: entity?.metadata?.exemption || false,
          stateIssued: entity?.metadata?.stateIssued || [],
          docData: entity?.metadata?.docData || ''
        };
        setShowShareable(
          entity?.metadata?.isShareable !== undefined ? entity?.metadata?.isShareable : true
        );
        setShowMultiState(
          entity?.metadata?.isMultiState !== undefined ? entity?.metadata?.isMultiState : false
        );
        setShowExemption(
          entity?.metadata?.isExemption !== undefined ? entity?.metadata?.isExemption : false
        );

        setFormData(formPayload);
      }
      setShowForm(true);
    },
    [propDocumentTypes]
  );

  // Set initial document type and config when props change
  useEffect(() => {
    const initializeDocument = async () => {
      setIsLoading(true);
      try {
        // If only one document type, select it automatically (only possible on update functionality)
        if (propDocumentTypes.length === 1) {
          handleUpdateMetaData(propDocumentTypes[0]);
        } else {
          // Check for onboarding document type ( as we don't need that option for onboarding page)
          const firstDocType = propDocumentTypes[0];
          if (firstDocType?.ssn) {
            setFormData((prev) => ({
              ...prev,
              ssn: firstDocType?.ssn
            }));
          }
          setShowShareable(
            firstDocType?.isShareable !== undefined ? firstDocType?.isShareable : true
          );
          setShowMultiState(
            firstDocType?.isMultiState !== undefined ? firstDocType?.isMultiState : false
          );
          setShowExemption(
            firstDocType?.isExemption !== undefined ? firstDocType?.isExemption : false
          );
        }
        setFetchError(null);
      } catch (error) {
        console.error('Error initializing document:', error);
        setFetchError('Unable to initialize document. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeDocument();
    // eslint-disable-next-line
  }, [propDocumentTypes]);

  // Find and set the selected document type object when ID changes
  useEffect(() => {
    if (selectedDocType && propDocumentTypes && propDocumentTypes.length > 0) {
      const docType = propDocumentTypes.find((dt) => dt.coreVerifyId === selectedDocType);
      setSelectedDocTypeObj(docType || null);
    } else {
      setSelectedDocTypeObj(null);
    }

    // Check if any document has isDocSelected: true and select it
    const selectedDoc = propDocumentTypes?.find((doc) => doc.isDocSelected === true);
    if (selectedDoc) {
      if (selectedDoc.coreVerifyId) {
        setSelectedDocType(selectedDoc.coreVerifyId);
      }
      setSelectedDocTypeObj(selectedDoc);
      setIsPreSelected(true);
      setHideTitle(selectedDoc.hideTitle === true);
      setHideFileView(selectedDoc.hideFileView === true);
      handleUpdateMetaData(selectedDoc);
    } else if (propDocumentTypes?.length === 1) {
      if (propDocumentTypes[0]?.coreVerifyId) {
        setSelectedDocType(propDocumentTypes[0]?.coreVerifyId);
      }
      setSelectedDocTypeObj(propDocumentTypes[0]);
      setIsPreSelected(false);
      setHideTitle(propDocumentTypes[0]?.hideTitle === true);
      setHideFileView(propDocumentTypes[0]?.hideFileView === true);
    } else {
      setIsPreSelected(false);
      setHideTitle(false);
      setHideFileView(false);
    }
  }, [selectedDocType, propDocumentTypes, handleUpdateMetaData]);

  // Get document configuration from backend when document type is selected
  useEffect(() => {
    if (selectedDocType) {
      fetchDocConfig(selectedDocType);
    }
  }, [selectedDocType, fetchDocConfig]);

  const resetFormData = () => {
    setFormData(defaultValues);
    setFrontUploaded(false);
    setBackUploaded(false);
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Use moment.parseZone for date fields to preserve timezone information
    if (name === 'effectiveDate' || name === 'expiryDate') {
      if (value) {
        // Parse date with moment.parseZone to avoid timezone shifts
        const parsed = moment.parseZone(value, 'YYYY-MM-DD', true);
        if (parsed.isValid()) {
          processedValue = parsed.format('YYYY-MM-DD');
        }
      }
    }

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: processedValue
      };
      if (name === 'multiState') {
        next.stateIssued = [];
      }
      return next;
    });

    if (name === 'jurisdiction') {
      setError((prev) => ({
        ...prev,
        jurisdiction: ''
      }));
    }

    if (name === 'multiState') {
      setError((prev) => ({
        ...prev,
        stateIssued: ''
      }));
    }
  };

  const handleSelectChange = (e) => {
    setSelectedDocType(e.target.value);
  };

  // Helper function to determine if we're dealing with a card number document or license number document
  const isCardNumberDocument = () => {
    return documentType !== 'document';
  };

  useEffect(() => {
    const isFrontRequired =
      docConfig?.front_attachment?.required && docConfig?.front_attachment?.mandatory;
    const isBackRequired =
      docConfig?.back_attachment?.required && docConfig?.back_attachment?.mandatory;

    if (isFrontRequired && isBackRequired) {
      if (frontUploaded && backUploaded) {
        setShowForm(true);
      }
    } else if (isFrontRequired && !isBackRequired) {
      if (frontUploaded) {
        setShowForm(true);
      }
    } else if (!isFrontRequired && !isBackRequired) {
      setShowForm(true);
    }
  }, [docConfig, frontUploaded, backUploaded]);

  const handleFileProcessed = (data, side, fileData) => {
    // Update form with OCR extracted data
    const newFormData = { ...formData };
    if (data?.card_number || data?.license_number) {
      newFormData[documentType === 'document' ? 'licenseNumber' : 'cardNumber'] =
        data?.card_number || data?.license_number;
    }

    if (data.valid_until) {
      newFormData.expiryDate = formatDate(data.valid_until);
    }

    if (data.issue_date) {
      newFormData.effectiveDate = formatDate(data.issue_date);
    }

    if (data?.card_type) {
      newFormData.card_type = data?.card_type;
    }

    if (data?.ssn) {
      newFormData.ssn = validation.removeHyphens(data?.ssn);
      newFormData.ssnFormat = validation.formatToSSN(newFormData.ssn);
    }

    if (data?.social_security_number) {
      newFormData.ssn = validation.removeHyphens(data?.social_security_number);
      newFormData.ssnFormat = validation.formatToSSN(newFormData.ssn);
    }

    if (side === 'front') {
      setUploadedFileFront(fileData);
    } else {
      setUploadedFileBack(fileData);
    }

    setFormData(newFormData);

    // Update upload status and clear related errors
    if (side === 'front') {
      setFrontUploaded(true);
      // Clear front attachment error if it exists
      if (error?.frontAttachment) {
        setError({
          ...error,
          frontAttachment: null
        });
      }
    } else if (side === 'back') {
      setBackUploaded(true);
      // Clear back attachment error if it exists
      if (error?.backAttachment) {
        setError({
          ...error,
          backAttachment: null
        });
      }
    }
  };
  const handleFailedUpload = (side, fileData) => {
    if (side === 'front') {
      setUploadedFileFront(fileData);
    } else {
      setUploadedFileBack(fileData);
    }
    setShowForm(true);
  };

  // Helper function to format dates from API to YYYY-MM-DD format for input[type=date]
  const formatDate = (dateString) => {
    if (!dateString) return '';

    // Use moment.parseZone to parse dates without timezone conversion
    // Try multiple formats: MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD, ISO formats
    const parsed = moment.parseZone(
      dateString,
      ['MM/DD/YYYY', 'M/D/YYYY', 'MM-DD-YYYY', 'M-D-YYYY', 'YYYY-MM-DD', moment.ISO_8601],
      true
    );

    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }

    // Fallback: try non-strict parsing
    const fallback = moment.parseZone(dateString);
    if (fallback.isValid()) {
      return fallback.format('YYYY-MM-DD');
    }

    return '';
  };

  // Render jurisdiction field separately
  const renderJurisdictionField = () => {
    if (!docConfig?.specialities_enabled) {
      return null;
    }

    return (
      <div className="form-group">
        <label>Jurisdiction {<span className="required">*</span>}</label>
        <select
          className="form-control"
          name="jurisdiction"
          value={formData.jurisdiction}
          onChange={handleInputChange}
        >
          <option value="" disabled>
            Select Jurisdiction
          </option>
          {stateOptions.map((state) => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>
        {error.jurisdiction && <p className="error">{error.jurisdiction}</p>}
      </div>
    );
  };

  // Render custom doc data field - ONLY uses require_doc_data, doc_data_options, doc_data_name
  const renderDocDataFields = () => {
    if (!docConfig?.require_doc_data || !docConfig?.doc_data_options || !docConfig?.doc_data_name) {
      return null;
    }

    try {
      const options =
        typeof docConfig.doc_data_options === 'string'
          ? JSON.parse(docConfig.doc_data_options)
          : docConfig.doc_data_options;

      // Only render if options is an object (not an array)
      if (!Array.isArray(options) && typeof options === 'object') {
        return (
          <div className="form-group">
            <label>
              {docConfig.doc_data_name} {<span className="required">*</span>}
            </label>
            <select
              className="form-control"
              name="docData"
              value={formData.docData || ''}
              onChange={(e) => {
                handleInputChange(e);
                // Clear error when user selects
                if (error.docData) {
                  setError((prev) => ({ ...prev, docData: '' }));
                }
              }}
            >
              <option value="" disabled>
                Select {docConfig.doc_data_name}
              </option>
              {Object.entries(options).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
            {error.docData && <p className="error">{error.docData}</p>}
          </div>
        );
      }

      return null;
    } catch (error) {
      console.error('Error parsing doc_data_options:', error);
      return null;
    }
  };

  // Function to replace placeholders in URL template
  const replacePlaceholders = (urlTemplate, data) => {
    const firstDocType = propDocumentTypes[0];
    if (!data?.ssn && firstDocType?.ssn) {
      data.ssn = firstDocType?.ssn;
      data.ssnFormat = validation.formatToSSN(firstDocType?.ssn);
    }
    if (firstDocType?.firstName) {
      data.firstName = firstDocType?.firstName;
    }
    if (firstDocType?.lastName) {
      data.lastName = firstDocType?.lastName;
    }
    let url = urlTemplate;

    // Map placeholders to possible data keys in fallback order
    const placeholderMap = {
      licenseNumber: ['licenseNumber', 'cardNumber', 'card_code'],
      cardNumber: ['cardNumber', 'licenseNumber', 'card_code'],
      card_code: ['cardNumber', 'licenseNumber', 'card_code']
      // Add more mappings if needed
    };

    // Match all placeholders in the URL like {licenceNumber}
    const matches = url.match(/{(\w+)}/g);
    if (!matches) return url;

    matches?.forEach((placeholder) => {
      const key = placeholder.replace(/[{}]/g, '');
      const fallbackKeys = placeholderMap[key] || [key]; // Use mapping or just itself

      for (const fallbackKey of fallbackKeys) {
        if (data[fallbackKey]) {
          url = url.replace(new RegExp(placeholder, 'g'), data[fallbackKey]);
          break;
        }
      }
    });
    return url;
  };

  // console.log("docConfig", docConfig)
  // Function to make verification API call based on configuration
  const verifyDocument = async () => {
    if (!docConfig || !docConfig.verification_config) {
      console.error('Verification configuration not found');
      return false;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      // Parse verification config
      const verConfig =
        typeof docConfig.verification_config === 'string'
          ? JSON.parse(docConfig.verification_config)
          : docConfig.verification_config;

      // Check if we need jurisdiction-specific verification URL
      let verificationUrl = '';
      let method = 'GET';

      if (verConfig.jurisdiction_map && formData.jurisdiction) {
        // Get jurisdiction-specific URL
        const urlTemplate = verConfig.jurisdiction_map[formData.jurisdiction];
        if (urlTemplate) {
          verificationUrl = replacePlaceholders(urlTemplate, formData);
          method = verConfig.default?.method || 'GET';
        } else {
          console.error(`No verification URL for jurisdiction: ${formData.jurisdiction}`);
          setVerificationResult({
            success: false,
            error: `No verification available for jurisdiction: ${formData.jurisdiction}`
          });
          setIsVerifying(false);
          return false;
        }
      } else if (verConfig.url) {
        // Use default URL
        verificationUrl = replacePlaceholders(verConfig.url, formData);
        method = verConfig.method || 'GET';
      } else {
        console.error('No verification URL found in configuration');
        setVerificationResult({
          success: false,
          error: 'No verification URL configured'
        });
        setIsVerifying(false);
        return false;
      }

      // Prepare data to send based on the fields specified in the config
      const requestData = {};
      const fields = verConfig.fields || verConfig.default?.fields || [];

      if (Array.isArray(fields)) {
        fields.forEach((field) => {
          if (formData[field] !== undefined && formData[field] !== '') {
            requestData[field] = formData[field];
          }
        });
      }

      // For POST requests to medical verification endpoints, include file data
      if (method.toUpperCase() === 'POST' && verificationUrl.includes('analyze_')) {
        // Add file data for medical verification
        if (uploadedFileFront?.binaryData) {
          requestData.file = uploadedFileFront.binaryData;
          requestData.fileName = uploadedFileFront.name;
          requestData.fileType = uploadedFileFront.name.split('.').pop().toLowerCase();
        }

        // Add document type information
        requestData.documentType = docConfig?.title || selectedDocType;
        requestData.expectedType = selectedDocType;
      }

      // Create a new axios instance with retry configuration
      const axiosInstance = axios.create();
      axiosRetry(axiosInstance, {
        retries: 2,
        retryDelay: (retryCount) => {
          return retryCount * 1000; // time interval between retries
        },
        retryCondition: (error) => {
          return (
            axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status >= 400
          ); // retry on server errors
        }
      });

      // Make the API call based on the method
      let response;
      if (method.toUpperCase() === 'GET') {
        // For GET with URL template, we already included params in the URL
        if (verificationUrl.includes('?')) {
          response = await axiosInstance.get(verificationUrl);
        } else {
          // Add query params for GET
          const params = new URLSearchParams();
          Object.entries(requestData).forEach(([key, value]) => {
            params.append(key, value);
          });
          response = await axiosInstance.get(`${verificationUrl}?${params.toString()}`);
        }

        let validation_config = {};
        try {
          validation_config = JSON.parse(docConfig?.validation_config);
          if (formData.jurisdiction && validation_config[formData.jurisdiction]) {
            validation_config = validation_config[formData.jurisdiction];
          }
        } catch (e) {}
        let isVerified = validation.validateDocument(validation_config, formData, response.data);
        console.log('----isVerified-----', isVerified);
        response.data.verified = isVerified;
      } else {
        // Default to POST
        if (verificationUrl.includes('analyze_') && uploadedFileFront?.binaryData) {
          // For medical verification endpoints, use FormData (multipart/form-data)
          const uploadFormData = new FormData();

          // Create a Blob from the binary data
          const fileBlob = new Blob([uploadedFileFront.binaryData], {
            type: uploadedFileFront.name
              ? uploadedFileFront.name.endsWith('.pdf')
                ? 'application/pdf'
                : uploadedFileFront.name.match(/\.(jpg|jpeg)$/i)
                  ? 'image/jpeg'
                  : uploadedFileFront.name.endsWith('.png')
                    ? 'image/png'
                    : 'application/octet-stream'
              : 'application/octet-stream'
          });

          // Add ONLY the file to FormData - medical endpoints just need the file
          uploadFormData.append('file', fileBlob, uploadedFileFront.name);

          response = await axiosInstance.post(verificationUrl, uploadFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // For other POST endpoints, send JSON data
          response = await axiosInstance.post(verificationUrl, requestData);
        }
        // Validate POST response
        let validation_config = {};
        try {
          validation_config = JSON.parse(docConfig?.validation_config);
          if (formData.jurisdiction && validation_config[formData.jurisdiction]) {
            validation_config = validation_config[formData.jurisdiction];
          }
        } catch (e) {
          console.error('❌ [POST] Error parsing validation_config:', e);
        }
        let isVerified = validation.validateDocument(validation_config, formData, response.data);
        response.data.verified = isVerified;
      }

      // Process response
      setVerificationResult({
        success: true,
        data: {
          ...response.data,
          uploadedFileFront: uploadedFileFront,
          uploadedFileBack: uploadedFileBack
        }
      });

      return true;
    } catch (error) {
      console.error('Error verifying document:', error);
      sendDataToParent();
      // if verification fails, then upload file to s3 and send data to parent
      setVerificationResult({
        success: false,
        error: error.response?.data?.error || 'Verification failed'
      });

      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const validateForm = async () => {
    if (docConfig?.specialities_enabled && !formData.specialties?.length) {
      // check if selected doc has zero specialties then don't show error
      const selectedDoc = await propDocumentTypes?.find(
        (item) => item?.coreVerifyId === selectedDocType
      );
      if (selectedDoc?.specialities?.length > 0) {
        setError({
          ...error,
          specialties: 'Please select at least one specialty to verify your certificate'
        });
        return false;
      }
    }
    // Validate jurisdiction - required when specialities_enabled is true
    if (docConfig?.specialities_enabled && !formData.jurisdiction) {
      setError({
        ...error,
        jurisdiction: 'Please select jurisdiction'
      });
      return false;
    }

    // Validate custom doc data field - only when require_doc_data is true
    if (docConfig?.require_doc_data && docConfig?.doc_data_name && !formData.docData) {
      setError({
        ...error,
        docData: `Please select ${docConfig.doc_data_name}`
      });
      return false;
    }

    // Validate stateIssued (Additional compact states) - required when multiState is "yes"
    if (showMultiState && formData.multiState === 'yes') {
      if (!formData.stateIssued || formData.stateIssued.length === 0) {
        setError({
          ...error,
          stateIssued: 'Please select at least one additional state for multi-state license'
        });
        return false;
      }
    }

    // Validate file uploads
    // Check if front attachment is required and mandatory
    if (docConfig?.front_attachment?.required && docConfig?.front_attachment?.mandatory) {
      const hasFrontFile =
        uploadedFileFront || getFileNameFromSignedUrl(formData?.signedUrls, 'front')?.fileName;
      if (!hasFrontFile) {
        setError({
          ...error,
          frontAttachment: `Please upload the front of ${documentType === 'document' ? 'document' : 'certificate'}`
        });
        return false;
      }
    }

    // Check if back attachment is required and mandatory
    if (docConfig?.back_attachment?.required && docConfig?.back_attachment?.mandatory) {
      const hasBackFile =
        uploadedFileBack || getFileNameFromSignedUrl(formData?.signedUrls, 'back')?.fileName;
      if (!hasBackFile) {
        setError({
          ...error,
          backAttachment: `Please upload the back of ${documentType === 'document' ? 'document' : 'certificate'}`
        });
        return false;
      }
    }

    return true;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Set verifying state before async operations
    setIsVerifying(true);

    try {
      // Verify document first
      //  const isVerified = await verifyDocument();
      //  if (!isVerified) {
      //    // if somehow verification still fails then upload file to s3 and send data to parent
      //    sendDataToParent();
      //  }
      if (docConfig?.verification_config) {
        console.error('Verification configuration found');
        const verConfig =
          typeof docConfig.verification_config === 'string'
            ? JSON.parse(docConfig.verification_config)
            : docConfig.verification_config;
        console.log('----verConfig-----', verConfig);
      }
      const firstDocType = propDocumentTypes[0];
      onFileSubmit({
        cv_id: docConfig?.core_verify_id,
        formData: { ...formData, firstDocType: firstDocType },
        url: `${REQUEST_ENDPOINT}/api/create-job`
      });
      await sendDataToParent();
    } finally {
      setIsVerifying(false);
    }
  };
  const convertToBase64 = (file, type) => {
    // Check if file is valid before attempting conversion
    if (!file) {
      console.error('Invalid file provided to convertToBase64');
      return null;
    }

    // Helper to get mime type from file name/extension
    const getMimeType = (fileName) => {
      const ext = fileName && fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
      const mimeTypes = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        pdf: 'application/pdf',
        webp: 'image/webp'
      };
      if (type && mimeTypes[type]) {
        return mimeTypes[type];
      }
      if (ext && mimeTypes[ext]) {
        return mimeTypes[ext];
      }
      return 'image/png';
    };

    // Handle ArrayBuffer input
    if (file instanceof ArrayBuffer) {
      // Convert ArrayBuffer to base64
      let binary = '';
      const bytes = new Uint8Array(file);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const mimeType = getMimeType(file.name || '');
      return Promise.resolve(`data:${mimeType};base64,${btoa(binary)}`);
    }

    // Handle Blob/File input
    if (file instanceof Blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve(reader.result); // Keep full data URL including mime type
        };
        reader.onerror = (error) => reject(error);
      });
    }
    return null;
  };

  const createFileMetadata = async (file) => {
    const fileExt = file.name.split('.').pop().toLowerCase();
    const base64String = await convertToBase64(file.binaryData, fileExt);
    return {
      base64String: base64String,
      name: file.name
    };
  };
  const sendDataToParent = async (updatedKeys) => {
    const payloadKeys = updatedKeys ? updatedKeys : formData;
    let selectedEntity = propDocumentTypes?.find((item) => item?.coreVerifyId === selectedDocType);
    if (!selectedEntity && propDocumentTypes?.length === 1) {
      selectedEntity = selectedDocTypeObj || propDocumentTypes[0];
    }

    // If backend S3 upload is required and uploadFor provided, upload files and send keys
    let s3Keys = null;
    if (uploadFor && isS3UploadRequired) {
      const filesToUpload = [];
      if (uploadedFileFront?.binaryData)
        filesToUpload.push({ side: 'front', fileData: uploadedFileFront });
      if (uploadedFileBack?.binaryData)
        filesToUpload.push({ side: 'back', fileData: uploadedFileBack });
      if (filesToUpload.length) {
        try {
          const uploaded = await uploadFilesViaApi(filesToUpload);
          s3Keys = uploaded.keys || null;
          // Clear upload error on success
          if (error.upload) {
            setError({ ...error, upload: null });
          }
        } catch (e) {
          console.error('Error uploading files via API:', e);
          // Error is already set in uploadFilesViaApi
          return; // Don't proceed with sending data if upload failed
        }
      }
    }

    const frontFile =
      !isS3UploadRequired && uploadedFileFront?.binaryData
        ? await createFileMetadata(await convertHeicIfNeeded(uploadedFileFront))
        : null;
    const backFile =
      !isS3UploadRequired && uploadedFileBack?.binaryData
        ? await createFileMetadata(await convertHeicIfNeeded(uploadedFileBack))
        : null;
    const backendPayload = {
      title: selectedEntity?.title,
      entityId: selectedEntity?.id,
      documentTypeId: selectedDocTypeObj?.document_type_id,
      documentType: selectedEntity?.documentType,
      coreVerifyId: selectedDocType || null,
      files: {
        front: frontFile,
        back: backFile
      },
      s3UploadedFileKeys: s3Keys,
      verificationResult: verificationResult?.data
    };

    // Only include documentNumber if required
    if (docConfig?.require_number) {
      backendPayload.documentNumber = payloadKeys.cardNumber || payloadKeys.licenseNumber || '';
    }

    // Only include effectiveDate if required
    if (docConfig?.require_valid_date && payloadKeys.effectiveDate) {
      backendPayload.effectiveDate = payloadKeys.effectiveDate;
    }

    // Only include expiryDate if required
    if (docConfig?.require_expire_date && payloadKeys.expiryDate) {
      backendPayload.expiryDate = payloadKeys.expiryDate;
    }

    // Only include jurisdiction if specialities_enabled
    if (docConfig?.specialities_enabled && payloadKeys.jurisdiction) {
      backendPayload.jurisdiction = payloadKeys.jurisdiction;
    }

    // Only include custom doc data if require_doc_data is true
    if (docConfig?.require_doc_data && payloadKeys.docData) {
      backendPayload.docData = payloadKeys.docData;
    }

    // Only include specialties if enabled
    if (docConfig?.specialities_enabled && payloadKeys.specialties) {
      backendPayload.specialties = payloadKeys.specialties;
    }

    // Only include shareable if enabled
    if (docConfig?.shareable && showShareable) {
      backendPayload.shareable = payloadKeys.shareable;
    }

    // Only include multiState fields if shown
    if (showMultiState) {
      backendPayload.multiState = payloadKeys.multiState;
      if (payloadKeys.multiState === 'yes' && payloadKeys.stateIssued) {
        backendPayload.stateIssued = payloadKeys.stateIssued;
      }
    }

    // Only include exemption if shown (matching the render condition)
    const exemptionEntity = propDocumentTypes?.find(
      (item) => item?.coreVerifyId === selectedDocType
    );
    if ((showExemption || exemptionEntity?.isExemption) && payloadKeys.exemption !== undefined) {
      backendPayload.exemption = payloadKeys.exemption;
    }

    const payload = {
      type: 'DOCUMENT_VERIFICATION_SUCCESS',
      data: backendPayload
    };
    window.parent.postMessage(JSON.stringify(payload), '*');
  };

  const handleCancel = () => {
    const payload = {
      type: 'DOCUMENT_VERIFICATION_CANCELLED'
    };
    window.parent.postMessage(JSON.stringify(payload), '*');
  };

  const handleDelete = (e) => {
    e.preventDefault();
    const payload = {
      type: 'DOCUMENT_DELETE'
    };
    window.parent.postMessage(JSON.stringify(payload), '*');
  };

  useEffect(() => {
    if (verificationResult?.success) {
      sendDataToParent();
    }
    // eslint-disable-next-line
  }, [verificationResult]);

  // -------------------------------------------------------------

  // -------------------------------------------------------------

  const renderMultiSelectField = () => {
    if (!showMultiState) return null;
    const handleStateChange = (event) => {
      const {
        target: { value }
      } = event;
      setFormData((prev) => ({
        ...prev,
        stateIssued: typeof value === 'string' ? value.split(',') : value
      }));
      // Clear stateIssued error when user selects states
      if (error.stateIssued) {
        setError((prev) => ({
          ...prev,
          stateIssued: ''
        }));
      }
    };

    const getSelectedStateLabels = () => {
      return formData.stateIssued
        .map((value) => {
          const state = stateOptions.find((option) => option.value === value);
          return state ? state.label : value;
        })
        .join(', ');
    };

    return (
      <div>
        <div className="form-group">
          <label>Multi State {<span className="required">*</span>}</label>
          <select
            className="form-control"
            name="multiState"
            value={formData.multiState}
            onChange={handleInputChange}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {formData.multiState === 'yes' && (
          <div className="form-group">
            <label>State Issued {<span className="required">*</span>}</label>
            <FormControl fullWidth>
              <Select
                multiple
                value={formData.stateIssued}
                onChange={handleStateChange}
                input={<OutlinedInput />}
                renderValue={() => getSelectedStateLabels()}
              >
                {stateOptions.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    <Checkbox checked={formData.stateIssued.indexOf(state.value) > -1} />
                    <ListItemText primary={state.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {error.stateIssued && <p className="error">{error.stateIssued}</p>}
          </div>
        )}
      </div>
    );
  };
  const renderExemptionField = () => {
    let entity = propDocumentTypes?.find((item) => item?.coreVerifyId === selectedDocType);

    if (!showExemption && !entity?.isExemption) return null;
    return (
      <div>
        <div className="form-group" style={{ flexDirection: 'row', gap: '8px' }}>
          <input
            type="checkbox"
            name="exemption"
            // className="form-control"
            style={{
              width: '15px',
              accentColor: '#ff80bf', // Changes checkbox color
              cursor: 'pointer'
            }}
            checked={formData.exemption}
            onChange={(e) =>
              handleInputChange({
                target: {
                  name: 'exemption',
                  value: e.target.checked ? true : false
                }
              })
            }
          />
          <label className="checkbox-container">Exemption</label>
        </div>
      </div>
    );
  };

  const renderSpecialtiesField = () => {
    if (!docConfig?.specialities_enabled) {
      return null;
    }
    let options = propDocumentTypes?.find((item) => item?.coreVerifyId === selectedDocType);
    let selectOptions = options?.specialities?.length
      ? options.specialities.map((specialty) => ({
          value: specialty.value || specialty.id,
          label: specialty.label || specialty.name
        }))
      : [];

    const handleSpecialtiesChange = (event) => {
      const {
        target: { value }
      } = event;
      setFormData((prev) => ({
        ...prev,
        specialties: typeof value === 'string' ? value.split(',') : value
      }));
      setError({
        ...error,
        specialties: ''
      });
    };

    const getSelectedSpecialtyLabels = () => {
      return formData.specialties
        .map((value) => {
          const specialty = selectOptions.find((option) => option.value === value);
          return specialty ? specialty.label : value;
        })
        .join(', ');
    };

    return (
      <div className="form-group">
        <label>Specialties {<span className="required">*</span>}</label>
        <FormControl fullWidth>
          <Select
            multiple
            value={formData.specialties}
            onChange={handleSpecialtiesChange}
            input={<OutlinedInput />}
            renderValue={() => getSelectedSpecialtyLabels()}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={formData.specialties.indexOf(option.value) > -1} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error.specialties && <p className="error">{error.specialties}</p>}
      </div>
    );
  };

  // -------------------------------------------------------------
  const renderFileViews = () => {
    if (hideFileView) return null;

    if (!formData?.signedUrls?.length) return null;

    return (
      <div className="file-preview-container">
        {formData.signedUrls.map((item, index) => {
          let label;
          if (item.key.includes('front_attachment')) {
            label = 'Front';
          } else if (item.key.includes('back_attachment')) {
            label = 'Back';
          } else {
            label = `File${index + 1}`;
          }

          return (
            <div key={index} className="file-preview form-group">
              <label>{label}</label>
              <a href={item.file} target="_blank" className="link_preview" rel="noreferrer">
                (View)
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  const renderVerificationFileViews = () => {
    if (hideFileView) return null;

    if (!formData?.verificationSignedUrls?.length) return null;

    return (
      <div className="file-preview-container">
        {formData.verificationSignedUrls.map((item, index) => (
          <div key={`verification-${index}`} className="file-preview form-group">
            <label>
              Verification Results
              {formData.verificationSignedUrls.length > 1 ? ` File ${index + 1}` : ''}
            </label>
            <a href={item.file} target="_blank" className="link_preview" rel="noreferrer">
              (View)
            </a>
          </div>
        ))}
      </div>
    );
  };

  // Render the appropriate number field based on document type
  const renderNumberField = () => {
    if (!docConfig || !docConfig.require_number) {
      return null;
    }

    const isCardType = isCardNumberDocument();
    const labelText = documentType === 'document' ? 'Document Number' : 'License Number';

    const fieldName = isCardType ? 'cardNumber' : 'licenseNumber';
    const fieldValue = documentType === 'document' ? formData.licenseNumber : formData.cardNumber;

    return (
      <div className="form-group">
        <label>
          {labelText} {<span className="required">*</span>}
        </label>
        <input
          type="text"
          className="form-control"
          name={fieldName}
          value={fieldValue}
          onChange={handleInputChange}
          required={docConfig.require_number}
        />
      </div>
    );
  };

  const isRequireValidDateMandatory =
    docConfig?.require_valid_date && (docConfig?.require_valid_date_mandatory ?? true);
  const isRequireExpireDateMandatory =
    docConfig?.require_expire_date && (docConfig?.require_expire_date_mandatory ?? true);
  return (
    <div className="container_wrapper">
      {isLoading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <div>Loading...</div>
        </div>
      ) : (
        <>
          {propDocumentTypes?.length === 1 &&
          !propDocumentTypes[0]?.coreVerifyId &&
          propDocumentTypes[0]?.id ? (
            <div className="form-group">
              <label>{documentType === 'certificate' ? 'Certificate' : 'Document'} Title</label>
              <input
                type="text"
                className="form-control"
                value={propDocumentTypes[0]?.title || 'Document'}
                disabled
                readOnly
              />
            </div>
          ) : (
            propDocumentTypes?.length > 0 &&
            !hideTitle && (
              <div className="form-group">
                <label>
                  {documentType === 'certificate' ? 'Certificate' : 'Document'} Title{' '}
                  <span className="required">*</span>
                </label>
                <select
                  className="form-control"
                  value={selectedDocType}
                  onChange={(e) => {
                    resetFormData();
                    handleSelectChange(e);
                  }}
                  disabled={propDocumentTypes?.length === 1 || isPreSelected}
                >
                  <option value="">
                    Select {documentType === 'certificate' ? 'Certificate' : 'Document'} Type
                  </option>
                  {propDocumentTypes.map((doc) => (
                    <option key={doc?.coreVerifyId} value={doc?.coreVerifyId}>
                      {doc?.title}
                    </option>
                  ))}
                </select>
              </div>
            )
          )}

          {fetchError && (
            <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
              {fetchError}
            </div>
          )}
          {docConfig && !fetchError && (
            <>
              {/* File Upload Section */}
              {docConfig.front_attachment?.required && (
                <>
                  <FileUpload
                    label={`Front of ${documentType === 'document' ? 'Document' : 'Certificate'}`}
                    required={docConfig?.front_attachment?.mandatory}
                    side="front"
                    onFileProcessed={handleFileProcessed}
                    handleFailedUpload={handleFailedUpload}
                    selectedDocType={selectedDocType}
                    isUploading={frontUploading}
                    setIsUploading={setFrontUploading}
                    uploadedFileName={getFileNameFromSignedUrl(formData?.signedUrls, 'front')}
                  />
                  {error?.frontAttachment && (
                    <div
                      className="error-message"
                      style={{ color: 'red', margin: '-10px 0 10px 0', fontSize: '14px' }}
                    >
                      {error.frontAttachment}
                    </div>
                  )}
                </>
              )}

              {docConfig.back_attachment?.required && (
                <>
                  <FileUpload
                    label={`Back of ${documentType === 'document' ? 'Document' : 'Certificate'}`}
                    required={docConfig?.back_attachment?.mandatory}
                    side="back"
                    onFileProcessed={handleFileProcessed}
                    handleFailedUpload={handleFailedUpload}
                    selectedDocType={selectedDocType}
                    isUploading={backUploading}
                    setIsUploading={setBackUploading}
                    uploadedFileName={getFileNameFromSignedUrl(formData?.signedUrls, 'back')}
                  />
                  {error?.backAttachment && (
                    <div
                      className="error-message"
                      style={{ color: 'red', margin: '-10px 0 10px 0', fontSize: '14px' }}
                    >
                      {error.backAttachment}
                    </div>
                  )}
                </>
              )}

              {/* Document Details Form (shown after upload) */}
              {showForm && !frontUploading && !backUploading && (
                <form onSubmit={handleSubmit}>
                  {/* Specialty dropdown (if enabled) */}
                  {renderSpecialtiesField()}

                  {/* Jurisdiction field */}
                  {renderJurisdictionField()}

                  {/* Custom doc data field (uses require_doc_data, doc_data_options, doc_data_name) */}
                  {renderDocDataFields()}

                  {/* Number Field (License or Card based on document type) */}
                  {renderNumberField()}

                  {/* Effective Date Field */}
                  {docConfig.require_valid_date && (
                    <div className="form-group">
                      <label>
                        Effective Date{' '}
                        {isRequireValidDateMandatory ? <span className="required">*</span> : ''}
                      </label>
                      <input
                        type="date"
                        className="form-control date-picker"
                        name="effectiveDate"
                        value={formData.effectiveDate}
                        onChange={handleInputChange}
                        required={isRequireValidDateMandatory}
                      />
                    </div>
                  )}

                  {/* Expiry Date Field */}
                  {docConfig.require_expire_date && (
                    <div className="form-group">
                      <label>
                        Expiry Date{' '}
                        {isRequireExpireDateMandatory ? <span className="required">*</span> : ''}
                      </label>
                      <input
                        type="date"
                        className="form-control date-picker"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleInputChange}
                        required={isRequireExpireDateMandatory}
                      />
                    </div>
                  )}
                  {renderMultiSelectField()}
                  {renderExemptionField()}
                  {docConfig.shareable && showShareable && (
                    <div className="radio-container form-group">
                      <label>Do you want to mark this document as shareable ?</label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          value={'true'}
                          className="radio-input"
                          name="shareable"
                          checked={formData.shareable === 'true'}
                          onChange={handleInputChange}
                        />
                        <span className="custom-radio"></span>
                        Yes
                      </label>

                      <label className="radio-label">
                        <input
                          type="radio"
                          value={'false'}
                          name="shareable"
                          checked={formData.shareable === 'false'}
                          onChange={handleInputChange}
                          className="radio-input"
                        />
                        <span className="custom-radio"></span>
                        No
                      </label>
                    </div>
                  )}

                  {renderFileViews()}
                  {renderVerificationFileViews()}

                  {/* Upload Error */}
                  {error?.upload && (
                    <div
                      className="error-message"
                      style={{
                        color: 'red',
                        margin: '10px 0',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}
                    >
                      {error.upload}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="btn_group">
                    <button type="submit" className="btn submit-btn" disabled={isVerifying}>
                      {isVerifying
                        ? 'Verifying...'
                        : !isPreSelected && propDocumentTypes.length === 1
                          ? 'Update'
                          : 'Submit'}
                    </button>
                    {propDocumentTypes.length === 1 && formData?.showDelete && (
                      <button className="btn cancel-btn" onClick={handleDelete}>
                        Delete
                      </button>
                    )}
                    <button
                      className="btn cancel-btn"
                      onClick={handleCancel}
                      disabled={isVerifying}
                    >
                      {'Cancel'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentUpload;
